import React from "react"
import { useTheme } from '../contexts/ThemeContext';
import { Input } from "./ui/input"
import { Label } from "./ui/label"
import { Button } from "./ui/button"
import { Checkbox } from "./ui/checkbox"
import { useAuth } from '../contexts/AppContexts';
import { useContext } from 'react';
import { AuthContext } from '../contexts/AppContexts';

export type EmployeeFormValues = {
  name: string
  code: string
  email?: string
  role: "user" | "admin"
  level?:
    | "Bezirksdirektor"
    | "Generalagent II"
    | "Generalagent"
    | "Hauptagent"
    | "Agent"
    | "Innendienst"
    | "none"
    | "" // leerer String für keine Auswahl
  canEditRoster: boolean
  canManageGoals: boolean
  canViewStats: boolean
  adv?: string
  btnr?: string
  uNumber?: string
  avusPassword?: string
  canSendVerlaengerungEmail: boolean
}

type Props = {
  initial?: Partial<EmployeeFormValues>
  onSubmit?: (values: EmployeeFormValues) => void
  onCancel?: () => void
}

export function EmployeeEditForm({ initial, onSubmit, onCancel }: Props) {
  const { isDark } = useTheme();
  const auth = useAuth();
  const currentUserRole = auth.user?.role;
  console.log('[EmployeeEditForm] initial:', initial);
  console.log('[EmployeeEditForm] auth.user:', auth.user);
  const [values, setValues] = React.useState<EmployeeFormValues>({
    name: initial?.name ?? "",
    code: initial?.code ?? "",
    email: (initial as any)?.email ?? "",
    role: initial?.role ?? "user",
    level: (initial?.level as EmployeeFormValues["level"]) ?? "Generalagent",
    canEditRoster: initial?.canEditRoster ?? false,
    canManageGoals: initial?.canManageGoals ?? false,
    canViewStats: (initial as any)?.canViewStats ?? false,
    adv: initial?.adv ?? "",
    btnr: initial?.btnr ?? "",
    uNumber: initial?.uNumber ?? "",
    avusPassword: initial?.avusPassword ?? "",
    canSendVerlaengerungEmail: (initial as any)?.canSendVerlaengerungEmail ?? false,
  })

  const update = (patch: Partial<EmployeeFormValues>) =>
    setValues((v) => ({ ...v, ...patch }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const levelOut = values.level === "none" ? "" : values.level
    // Wenn levelOut leer ist, als 'none' speichern
    onSubmit?.({ ...values, level: levelOut === '' ? 'none' : levelOut })
  }

  return (
    <div className={`${isDark ? 'glass-card' : 'bg-white/90 border border-slate-200 shadow-lg'} rounded-3xl p-6 w-full max-w-md`}>
      <h3 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Mitarbeiter bearbeiten</h3>
      <form onSubmit={handleSubmit}>
        <div className="space-y-4">
          <Input
            name="name"
            placeholder="Name (Vorname Nachname)"
            required
            value={values.name}
            className={isDark ? "text-white placeholder:text-white/60" : "text-slate-900 placeholder:text-slate-400"}
            onChange={(e) => update({ name: e.target.value })}
          />

          {/* Login Code */}
          <div className={isDark ? "bg-blue-500/10 p-3 rounded-xl border border-blue-400/30" : "bg-blue-50 p-3 rounded-xl border border-blue-200"}>
            <Label className={`text-xs font-bold uppercase mb-1 block ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
              LOGIN CODE
            </Label>
            <Input
              name="code"
              placeholder="4-stellig"
              maxLength={4}
              required
              value={values.code}
              className={isDark ? "text-white placeholder:text-white/60" : "text-slate-900 placeholder:text-slate-400"}
              onChange={(e) => update({ code: e.target.value })}
            />
          </div>

          <div className="space-y-3">
            {/* Rolle (neu) */}
            <div className={isDark ? "bg-white/10 p-3 rounded-xl border border-white/20" : "bg-slate-50 p-3 rounded-xl border border-slate-200"}>
              <Label className={`text-xs font-bold uppercase mb-2 block ${isDark ? 'text-white/70' : 'text-slate-700'}`}>ROLLE</Label>
              <select
                value={values.role}
                onChange={e => update({ role: e.target.value as "user" | "admin" })}
                className={isDark ? "bg-transparent text-white border border-white/30 rounded-md px-3 py-2 w-full" : "bg-white text-slate-900 border border-slate-300 rounded-md px-3 py-2 w-full"}
                required
              >
                <option value="" disabled>Rolle wählen</option>
                <option value="user">Mitarbeiter</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            {/* Vermittler-Stufe (neu) */}
            <div className={isDark ? "bg-purple-500/10 p-3 rounded-xl border border-purple-400/30" : "bg-purple-50 p-3 rounded-xl border border-purple-200"}>
              <Label className={`text-xs font-bold uppercase mb-2 block ${isDark ? 'text-purple-300' : 'text-purple-700'}`}>VERMITTLER-STUFE</Label>
              <select
                value={values.level ?? "none"}
                onChange={e => update({ level: e.target.value as EmployeeFormValues["level"] })}
                className={isDark ? "bg-transparent text-white border border-purple-400/30 rounded-md px-3 py-2 w-full" : "bg-white text-slate-900 border border-purple-300 rounded-md px-3 py-2 w-full"}
                required
              >
                <option value="none">Nicht festgelegt</option>
                <option value="Bezirksdirektor">Bezirksdirektor</option>
                <option value="Generalagent II">Generalagent II</option>
                <option value="Generalagent">Generalagent</option>
                <option value="Hauptagent">Hauptagent</option>
                <option value="Agent">Agent</option>
                <option value="Innendienst">Innendienst</option>
              </select>
            </div>

            {/* Berechtigungen */}
            <div className={isDark ? "bg-white/10 p-3 rounded-xl border border-white/20 space-y-2" : "bg-slate-50 p-3 rounded-xl border border-slate-200 space-y-2"}>
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox
                  checked={values.canEditRoster}
                  onCheckedChange={(c) => update({ canEditRoster: Boolean(c) })}
                  className="h-5 w-5"
                />
                <span className={`text-sm font-bold ${isDark ? 'text-white' : 'text-slate-700'}`}>Darf Dienstplan bearbeiten</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox
                  checked={values.canManageGoals}
                  onCheckedChange={(c) => update({ canManageGoals: Boolean(c) })}
                  className="h-5 w-5"
                />
                <span className={`text-sm font-bold ${isDark ? 'text-green-300' : 'text-green-700'}`}>Darf Ziele verwalten</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox
                  checked={values.canViewStats}
                  onCheckedChange={(c) => update({ canViewStats: Boolean(c) })}
                  className="h-5 w-5"
                />
                <span className={`text-sm font-bold ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>Darf Statistik einsehen</span>
              </label>
              {/* Verlängerung per E-Mail erlaubt (Admin sieht immer Checkbox) */}
              {currentUserRole === 'admin' && (
                <label className="flex items-center gap-3 cursor-pointer">
                  <Checkbox
                    checked={values.canSendVerlaengerungEmail}
                    onCheckedChange={(c) => update({ canSendVerlaengerungEmail: Boolean(c) })}
                    className="h-5 w-5"
                  />
                  <span className={`text-sm font-bold ${isDark ? 'text-cyan-300' : 'text-cyan-700'}`}>Verlängerung per E-Mail erlaubt</span>
                </label>
              )}
            </div>
          </div>


          {/* ADV / BTNR */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              name="adv"
              placeholder="ADV Nummer"
              value={values.adv}
              className={isDark ? "text-white placeholder:text-white/60" : "text-slate-900 placeholder:text-slate-400"}
              onChange={(e) => update({ adv: e.target.value })}
            />
            <Input
              name="btnr"
              placeholder="Betreuernr. (BTNR)"
              value={values.btnr}
              className={isDark ? "text-white placeholder:text-white/60" : "text-slate-900 placeholder:text-slate-400"}
              onChange={(e) => update({ btnr: e.target.value })}
            />
          </div>

          {/* U-Nummer / AVUS */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              name="uNumber"
              placeholder="U-Nummer"
              value={values.uNumber}
              className={isDark ? "text-white placeholder:text-white/60" : "text-slate-900 placeholder:text-slate-400"}
              onChange={(e) => update({ uNumber: e.target.value })}
            />
            <Input
              name="avusPassword"
              placeholder="AVUS Kennwort"
              value={values.avusPassword}
              className={isDark ? "text-white placeholder:text-white/60" : "text-slate-900 placeholder:text-slate-400"}
              onChange={(e) => update({ avusPassword: e.target.value })}
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              className={isDark ? "flex-1 text-white border-white/30 hover:bg-white/10" : "flex-1 text-slate-700 border-slate-300 hover:bg-slate-100"}
              onClick={() => onCancel?.()}
            >
              Abbrechen
            </Button>
            <Button type="submit" className={isDark ? "flex-1" : "flex-1 bg-blue-600 hover:bg-blue-700 text-white"}>
              Speichern
            </Button>
          </div>
        </div>
      </form>
    </div>
  )
}

export default EmployeeEditForm
